import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { getSession } from '../auth/session';
import { queryClient, queryKeys } from './query';
import { ROUTE_MODULES } from './screen-modules';

/**
 * 🔴 WHAT THIS COMPANY HAS TURNED ON — ERP Phase 1a increment 4, moved onto TanStack Query at 1a.7.
 *
 * ## ⚠️ THE WEB HAD NEVER ASKED, AND THE ROADMAP SAID SO
 *
 * §1a's Delivers line promises a *"module-gated nav driven by `/me/context`"*. Nothing in `web/src`
 * called that route — `api` had exactly one named method, `memberships` — so every company saw every
 * link in the product whether they had bought the module or not.
 *
 * 🔬 THE ROUTE IS `/company/context`, NOT `/me/context`. Phase 1's own log records the deviation and
 * its reason: `/api/v1/me` is the caller, and this answer is about the COMPANY.
 *
 * ## 🔴 AND THIS IS NOT A SECURITY BOUNDARY
 *
 * ⚠️ **`ModuleGuard` on the server is the gate.** Everything here decides what is SHOWN. A module
 * hidden in the nav is still refused by the API if somebody types the URL, and a module wrongly left
 * visible is still refused when they click it. 🔬 That is why the failure direction is chosen
 * deliberately: an unknown route is treated as VISIBLE, because hiding something a customer paid for
 * is worse than showing a link their API will refuse.
 */

export interface CompanyContext {
  companyId: string;
  permissions: string[];
  enabledModules: string[];
  scopes: unknown;
  branches: unknown[];
  /**
   * DS-11b4 (F591): the VAT period containing the company's today — its name (`2026-10`) and whether posting into it is
   * open — or null for somebody who may not read periods (`context.service.ts`). Optional: a fixture without it is a
   * bar without the pill.
   */
  vatPeriod?: { label: string; status: 'open' | 'locked' } | null;
}

/**
 * 🔴 ONE CACHE, SHARED BY THE NAV AND THE ROUTE GUARD — and it is the library's, not mine.
 *
 * ## ⚠️ THIS WAS A HAND-ROLLED PROMISE CACHE THREE DAYS AGO
 *
 * A module-level `{ companyId, promise }`, written because the nav (React) and `beforeLoad` (not
 * React) both needed the answer and neither could hold it for the other. It worked, and it was
 * forty lines of cache invalidation that `@tanstack/react-query` has shipped in this repository's
 * `package.json` since Phase 1 without being imported once.
 *
 * 🔬 `fetchQuery` IS THE POINT OF THE MOVE. It is callable from outside React, and it shares the
 * exact cache `useQuery` reads — so the guard that refuses a deep link and the nav that hides the
 * link are answering from one fetch, with one invalidation, rather than from two caches that agree
 * by luck.
 */
export function loadCompanyContext(): Promise<CompanyContext | null> {
  const companyId = getSession()?.companyId ?? null;
  if (!companyId) return Promise.resolve(null);

  return queryClient.fetchQuery({
    queryKey: queryKeys.companyContext(companyId),
    queryFn: () => api.get<CompanyContext>('/company/context'),
    /**
     * 🔴 A FAILURE MEANS "DO NOT GATE", NOT "GATE EVERYTHING".
     *
     * ⚠️ If this fails — offline, a 403 for a member holding nothing, a server restart mid-navigation
     * — returning null leaves every link visible and every route open. The API still refuses what the
     * company has not bought, so the cost is a dead link; the alternative, a nav that empties itself
     * whenever the network hiccups, looks exactly like the product having lost the customer's data.
     */
  }).catch(() => null);
}

/**
 * Drop what was cached for every company. Called when the company changes.
 *
 * ⚠️ `removeQueries` rather than `invalidateQueries`: invalidation marks the answer stale and keeps
 * serving it until a refetch lands, which would gate the NEW company's nav on the OLD company's
 * licence for as long as the request takes.
 */
export function forgetCompanyContext(): void {
  queryClient.removeQueries({ queryKey: ['company-context'] });
}

/**
 * The context for rendering. `undefined` while it is in flight, `null` when there is no answer —
 * and the nav treats both as "show everything", for the reason above.
 */
export function useCompanyContext(): CompanyContext | null | undefined {
  const companyId = getSession()?.companyId ?? null;

  const { data, isPending } = useQuery({
    queryKey: queryKeys.companyContext(companyId),
    queryFn: () => api.get<CompanyContext>('/company/context'),
    // No company chosen yet: there is nothing to ask for, and asking would 400.
    enabled: companyId !== null,
  });

  if (companyId === null) return null;
  return isPending ? undefined : (data ?? null);
}

/**
 * 🔴 IS THIS ROUTE REACHABLE FOR THIS COMPANY?
 *
 * ⚠️ THE TWO UNKNOWNS BOTH ANSWER YES, AND THAT IS THE WHOLE DESIGN. A route this build has no
 * module for, and a context that could not be loaded, are both *"show it"* — because the only cost
 * of a wrong yes is a link the API refuses, while a wrong no hides something the customer is paying
 * for and gives them no way to find it.
 *
 * 🔬 `core` is never gated. Every company has it; gating it would be a way to render a product with
 * no nav at all.
 */
export function moduleEnabledFor(routePath: string, context: CompanyContext | null | undefined): boolean {
  const module = ROUTE_MODULES[routePath];
  if (!module || module === 'core') return true;
  if (!context) return true;
  return context.enabledModules.includes(module);
}

/** The module a route needs, or `null` when it needs none. Used by the guard to name the refusal. */
export function moduleOfRoute(routePath: string): string | null {
  const module = ROUTE_MODULES[routePath];
  return !module || module === 'core' ? null : module;
}
